import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { FormEvent } from "react";

import { Lines } from "@/components/marketing/Lines";
import MarketingLottie from "@/components/marketing/MarketingLottie";
import {
  submitEnterpriseTrial,
  type EnterpriseTrial,
} from "@/components/marketing/enterpriseTrial";
import {
  FormField,
  FormFooter,
  SentPanel,
  fieldErrors,
  useInquiry,
  type Field,
} from "@/components/marketing/inquiryForm";
import { HEADING_2 } from "@/components/marketing/marketingType";
import {
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LandingSection } from "../LandingPrimitives";
import { CHIP } from "./splitExtension";

type TrialForm = Laid<"trialForm">;
type Name = keyof EnterpriseTrial;

/** The published `.w-form-done` text, verbatim. */
const SENT_TEXT =
  "Thank you! Our Enterprise Account Management will get back to you within 24h";

/** The two lines the published form opens on, one to a row. */
const IDENTITY: Array<Field<Name>> = [
  {
    name: "fullName",
    label: "Full Name",
    type: "text",
    autoComplete: "name",
    maxLength: 256,
    minLength: 3,
    invalid: "Please enter your full name",
  },
  {
    name: "email",
    label: "Work Email",
    type: "email",
    autoComplete: "email",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your work email",
  },
];

/** `.pop-up_text-filed_wrap`: the company and the reader's title, side by side. */
const COMPANY: Array<Field<Name>> = [
  {
    name: "companyName",
    label: "Company Name",
    type: "text",
    autoComplete: "organization",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your company name",
  },
  {
    name: "jobTitle",
    label: "Job Title",
    type: "text",
    autoComplete: "organization-title",
    maxLength: 256,
    minLength: 1,
    invalid: "Please enter your job title",
  },
];

/**
 * The three questions, set as written. Each published one carries the name
 * field's own `data-msg`, "Please enter your full name", which fits none of
 * them, so the message is this form's own.
 */
const QUESTIONS: Array<Field<Name>> = [
  {
    name: "websites",
    label: "What websites are most critical for your data acquisition needs?",
    type: "text",
    autoComplete: "off",
    maxLength: 256,
    minLength: 3,
    invalid: "Please answer this question",
  },
  {
    name: "challenges",
    label: "What are your biggest challenges in accessing data?",
    type: "text",
    autoComplete: "off",
    maxLength: 256,
    minLength: 3,
    invalid: "Please answer this question",
  },
  {
    name: "expectations",
    label: "What do you expect to achieve at the end of this trial?",
    type: "text",
    autoComplete: "off",
    maxLength: 256,
    minLength: 3,
    invalid: "Please answer this question",
  },
];

/** Every field, in the order the form asks them and a failed submit walks them. */
const FIELDS = [...IDENTITY, ...COMPANY, ...QUESTIONS];

const EMPTY: EnterpriseTrial = {
  fullName: "",
  email: "",
  companyName: "",
  jobTitle: "",
  websites: "",
  challenges: "",
  expectations: "",
};

/** `.line_divider`. */
const RULE = {
  width: 1,
  height: "0.96px",
  backgroundColor: marketingPalette.outline,
} satisfies SxProps<Theme>;

/**
 * `.pop-up_form.is-flex` in `.client-form_right`: the fields at .4rem apart
 * over their own .75rem, a rule, then the disclaimer beside Send. Once it is
 * away, the thank-you takes the form's place, as the published one does.
 */
function EnterpriseTrialForm() {
  const {
    values,
    errors,
    status,
    setStatus,
    edit,
    firstProblem,
    fieldId,
    errorId,
  } = useInquiry(EMPTY);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const firstInvalid = firstProblem(fieldErrors(FIELDS, values));
    if (firstInvalid) {
      document.getElementById(fieldId(firstInvalid))?.focus();
      return;
    }
    setStatus("sending");
    setStatus((await submitEnterpriseTrial(values)) ? "sent" : "failed");
  }

  const field = (spec: Field<Name>, question?: boolean) => (
    <FormField
      key={spec.name}
      spec={spec}
      question={question}
      value={values[spec.name]}
      error={errors[spec.name]}
      fieldId={fieldId(spec.name)}
      errorId={errorId(spec.name)}
      onChange={(next) => edit(spec.name, next)}
    />
  );

  if (status === "sent") {
    return <SentPanel text={SENT_TEXT} />;
  }
  return (
    <Box
      component="form"
      onSubmit={submit}
      noValidate
      // `.pop-up_form.is-flex`'s .4rem at every step: the phone's .7rem is on
      // `.pop-up_form` alone, which the pair outranks.
      sx={{ display: "flex", flexDirection: "column", gap: "6.4px" }}
    >
      {IDENTITY.map((spec) => field(spec))}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          [TINY]: { display: "flex", flexDirection: "column" },
        }}
      >
        {COMPANY.map((spec) => field(spec))}
      </Box>
      {QUESTIONS.map((spec) => field(spec, true))}
      <Box sx={RULE} />
      <FormFooter status={status} />
      {/* `.w-embed`: the published form closes on an empty embed, one more
          .4rem gap under its controls. */}
      <Box aria-hidden />
    </Box>
  );
}

/** `.client-form_left`: the chip, the title, the line, and the animation under them. */
function Offer({ block }: { block: TrialForm }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "16px",
        width: 1,
        maxWidth: "452px",
        [NOT_WIDE]: { maxWidth: "none" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        {block.label ? <Box sx={CHIP}>{block.label}</Box> : null}
        <Typography component="h2" sx={HEADING_2}>
          <Lines text={block.title} />
        </Typography>
        {block.body ? (
          <Typography
            sx={{
              color: marketingPalette.ink,
              fontSize: 18,
              lineHeight: "150%",
              textWrap: "balance",
              [TINY]: { fontSize: 14, lineHeight: "140%" },
            }}
          >
            {block.body}
          </Typography>
        ) : null}
      </Box>
      {block.lottie && block.image ? (
        <Box aria-hidden sx={{ width: "80%", [NOT_WIDE]: { display: "none" } }}>
          <MarketingLottie path={block.image} loop play />
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * `.client-form_wrap`: a trial sign-up on its grey panel, the offer beside the
 * form's white card, stacked below the wide step and bare on a phone.
 */
export default function TrialFormBlock({ block }: { block: TrialForm }) {
  return (
    <LandingSection
      padding="none"
      sx={{
        pt: "112px",
        pb: "32px",
        [NOT_WIDE]: { pt: "80px", pb: "80px" },
        [TINY]: { pt: "64px", pb: "64px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "80px",
          maxWidth: "1152px",
          mx: "auto",
          p: "12px 12px 12px 64px",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          borderRadius: "23.04px",
          backgroundColor: "#F0F3F4",
          [NOT_WIDE]: { flexDirection: "column", gap: "32px", p: "32px" },
          [TINY]: { border: "none", borderRadius: 0, p: "16px" },
        }}
      >
        <Offer block={block} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: 1,
            p: "24px",
            borderRadius: "16px",
            backgroundColor: "common.white",
            [TINY]: {
              gap: "12px",
              p: 0,
              backgroundColor: marketingPalette.surface,
            },
          }}
        >
          <Box
            sx={{
              color: marketingPalette.ink,
              fontSize: 14.08,
              fontWeight: 500,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {block.formTitle}
          </Box>
          <Box sx={RULE} />
          <EnterpriseTrialForm />
        </Box>
      </Box>
    </LandingSection>
  );
}
